const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parse(value: string | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatPaidDay(
  value: string | Date | null | undefined,
  options: { zone?: 'utc' | 'local'; year?: boolean } = {},
): string | null {
  const date = parse(value);
  if (!date) return null;
  const utc = (options.zone ?? 'utc') === 'utc';
  const day = utc ? date.getUTCDate() : date.getDate();
  const month = MONTHS[utc ? date.getUTCMonth() : date.getMonth()];
  const year = utc ? date.getUTCFullYear() : date.getFullYear();
  return options.year === false ? `${day} ${month}` : `${day} ${month} ${year}`;
}

export interface PaidMomentOptions {
  /** An IANA zone such as "America/Chicago": the workspace's own. Without one the reader's clock is used, still named. */
  timeZone?: string | null;
  /** Print the year. By default it is printed only when it is not the current year. */
  year?: boolean;
}

/** "3:05 pm EDT": one clock form with its zone named, so a reader never has to guess whose clock it is. */
export function formatPaidTime(
  value: string | Date | null | undefined,
  options: Pick<PaidMomentOptions, 'timeZone'> = {},
): string | null {
  const date = parse(value);
  if (!date) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: options.timeZone || undefined,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).formatToParts(date);
    const part = (type: string): string => parts.find((entry) => entry.type === type)?.value ?? '';
    return `${part('hour')}:${part('minute')} ${part('dayPeriod').toLowerCase()} ${part('timeZoneName')}`;
  } catch {
    return null;
  }
}

/** "23 Oct, 3:05 pm EDT": the day as "23 Oct" and the time with its zone, in the workspace zone when one is given. */
export function formatPaidMoment(
  value: string | Date | null | undefined,
  options: PaidMomentOptions = {},
): string | null {
  const date = parse(value);
  if (!date) return null;
  const time = formatPaidTime(date, options);
  if (!time) return null;
  try {
    const zone = options.timeZone || undefined;
    const year = new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric' }).format(date);
    const thisYear = new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric' }).format(
      new Date(),
    );
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      day: 'numeric',
      month: 'short',
    }).formatToParts(date);
    const part = (type: string): string => parts.find((entry) => entry.type === type)?.value ?? '';
    const withYear = options.year ?? year !== thisYear;
    return `${part('day')} ${part('month')}${withYear ? ` ${year}` : ''}, ${time}`;
  } catch {
    return null;
  }
}
